import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Bell, Calendar, Gem, Globe, UserPlus, Users } from 'lucide-react'
import { usePageInfo } from '../../config/queries'
import { imageUrl } from '../../helpers'
import { Main } from '../../components'
import {
  NorthernSkyRaidToolsLink,
  TimelineRemindersLink,
} from '../../components/AddonLinks'
import { ImageViewerItem } from '../../components/ImageViewerDialog'
import HeroSection from './components/landing/HeroSection'
import FeatureGrid from './components/landing/FeatureGrid'
import { FeatureItem } from './components/landing/FeatureCard'
import FeatureDetailSection, {
  DetailSectionItem,
} from './components/landing/FeatureDetailSection'
import NotificationTemplatesDialog from './components/landing/NotificationTemplatesDialog'
import { RawBullet, visibleBullets } from './components/landing/bullets'
import ApiCallout from './components/landing/ApiCallout'
import DiscordCallout from './components/landing/DiscordCallout'
import DesktopCallout from './components/landing/DesktopCallout'
import CtaSection from './components/landing/CtaSection'
import GameVersions from './components/landing/GameVersions'
import Reveal from './components/landing/Reveal'

const screenshot = (name: string, title: string): ImageViewerItem => ({
  url: imageUrl(`examples/${name}.avif`),
  alt: title,
  title,
})

const Home = () => {
  const { t } = useTranslation()
  const {
    data: { gameVersion },
  } = usePageInfo()

  const spreadsheetScreenshot: ImageViewerItem = {
    live: screenshot(
      'midnight-spreadsheet',
      "Extensive spreadsheet with all your team's data",
    ),
    classic_anniversary: screenshot('anniversary-sheet', 'Anniversary Sheet'),
    classic_era: screenshot('era-sheet', 'Era Sheet'),
    classic_progression: screenshot(
      'classic-progression-sheet',
      'Classic Progression Sheet',
    ),
  }[gameVersion]

  const warcraftLogsEventsLabel = t(
    gameVersion === 'live'
      ? 'homepage.integration.warcraftLogs-events-live'
      : 'homepage.integration.warcraftLogs-events',
  )

  const features: FeatureItem[] = [
    {
      id: 'roster',
      icon: Users,
      title: t('homepage.feature.roster.title'),
      caption: t('homepage.feature.roster.caption'),
      integrations: ['wow', 'sheets'],
    },
    {
      id: 'loot',
      icon: Gem,
      title: t('homepage.feature.loot.title'),
      caption: t('homepage.feature.loot.caption'),
      integrations: ['raidbots', 'questionablyEpic', 'rcLootCouncil'],
      liveOnly: true,
    },
    {
      id: 'events',
      icon: Calendar,
      title: t(
        gameVersion === 'live'
          ? 'homepage.feature.events.title'
          : 'homepage.feature.events.title-classic',
      ),
      caption: t(
        gameVersion === 'live'
          ? 'homepage.feature.events.caption'
          : 'homepage.feature.events.caption-classic',
      ),
      integrations: [
        { key: 'discord', label: t('homepage.integration.discord-events') },
        {
          key: 'warcraftLogs',
          label: warcraftLogsEventsLabel,
        },
      ],
    },
    {
      id: 'recruitment',
      icon: UserPlus,
      title: t('homepage.feature.recruitment.title'),
      caption: t('homepage.feature.recruitment.caption'),
      integrations: [
        {
          key: 'discord',
          label: t('homepage.integration.discord-recruitment'),
        },
        {
          key: 'warcraftLogs',
          label: t('homepage.integration.warcraftLogs-recruitment'),
        },
      ],
    },
    {
      id: 'notifications',
      icon: Bell,
      title: t('homepage.feature.notifications.title'),
      caption: t('homepage.feature.notifications.caption'),
      integrations: [
        'email',
        {
          key: 'discord',
          label: t('homepage.integration.discord-notifications'),
        },
      ],
    },
    {
      id: 'home',
      icon: Globe,
      title: t('homepage.feature.home.title'),
      caption: t('homepage.feature.home.caption'),
      integrations: [],
      demoUrl: 'https://avalerion.eu',
    },
  ]

  const sections: DetailSectionItem[] = [
    {
      id: 'roster',
      icon: Users,
      heading: t('homepage.section.roster.heading'),
      description: t('homepage.section.roster.description'),
      bullets: visibleBullets(
        t('homepage.section.roster.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ).map((bullet) =>
        bullet.includes('spreadsheet') ? (
          <>
            {bullet.replace('spreadsheet', '')}{' '}
            <Link
              to='/demo'
              target='_blank'
              rel='noreferrer'
              className='text-primary hover:text-primary/90'
            >
              spreadsheet
            </Link>
          </>
        ) : (
          bullet
        ),
      ),
      integrations: ['wow', 'sheets'],
      screenshots: [
        screenshot('roster-setup', 'Team roster'),
        screenshot('audit', "View key insights of your team's characters"),
        spreadsheetScreenshot,
        screenshot(
          'roster-tracking',
          'Sync with in-game guild ranks automatically',
        ),
      ],
    },
    {
      id: 'loot',
      icon: Gem,
      heading: t('homepage.section.loot.heading'),
      description: t('homepage.section.loot.description'),
      bullets: visibleBullets(
        t('homepage.section.loot.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ),
      integrations: ['raidbots', 'questionablyEpic', 'rcLootCouncil'],
      liveOnly: true,
      screenshots: [
        screenshot(
          'wishlist-overview',
          'Wishlist overview of your entire team',
        ),
        screenshot(
          'wishlist-personal',
          'View your wishes and upload sim reports',
        ),
        screenshot('loot-history', 'Loot history stats (from RCLootCouncil)'),
        screenshot(
          'loot-history-table',
          'Loot history table (from RCLootCouncil)',
        ),
      ],
    },
    {
      id: 'events',
      icon: Calendar,
      heading: t('homepage.section.events.heading'),
      description: t('homepage.section.events.description'),
      bullets: visibleBullets(
        t('homepage.section.events.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ).map((bullet) =>
        bullet.includes('NSRT') ? (
          <>
            {bullet
              .split(/(NSRT|TimelineReminders)/)
              .map((part, index) =>
                part === 'NSRT' ? (
                  <NorthernSkyRaidToolsLink key={index} label='NSRT' />
                ) : part === 'TimelineReminders' ? (
                  <TimelineRemindersLink key={index} />
                ) : (
                  part
                ),
              )}
          </>
        ) : (
          bullet
        ),
      ),
      integrations: [
        {
          key: 'warcraftLogs',
          label: warcraftLogsEventsLabel,
        },
        { key: 'discord', label: t('homepage.integration.discord-events') },
      ],
      screenshots: [
        screenshot('events-detail', 'Plan your raid setup'),
        screenshot('events-overview', 'Stay on top of attendance'),
        screenshot('events-cooldowns', 'Plan cooldowns'),
        screenshot('events-assignments', 'Plan encounter assignments'),
        screenshot('events-insights', 'Event insights'),
      ],
    },
    {
      id: 'recruitment',
      icon: UserPlus,
      heading: t('homepage.section.recruitment.heading'),
      description: t('homepage.section.recruitment.description'),
      bullets: visibleBullets(
        t('homepage.section.recruitment.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ),
      integrations: [
        {
          key: 'discord',
          label: t('homepage.integration.discord-recruitment'),
        },
      ],
      screenshots: [
        screenshot('applicants-details', 'Evaluate applicant details'),
        screenshot('applicants-form', 'Example custom application form'),
        screenshot('applicants-chat', 'Chat with applicants'),
      ],
    },
    {
      id: 'notifications',
      icon: Bell,
      heading: t('homepage.section.notifications.heading'),
      description: t('homepage.section.notifications.description'),
      bullets: visibleBullets(
        t('homepage.section.notifications.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ).map((bullet) =>
        bullet.includes('templates') ? (
          <>
            {bullet.split(/(templates)/).map((part, index) =>
              part === 'templates' ? (
                <NotificationTemplatesDialog key={index}>
                  <button
                    type='button'
                    className='text-primary hover:text-primary/90'
                  >
                    templates
                  </button>
                </NotificationTemplatesDialog>
              ) : (
                part
              ),
            )}
          </>
        ) : (
          bullet
        ),
      ),
      integrations: [
        {
          key: 'discord',
          label: t('homepage.integration.discord-notifications'),
        },
        'email',
      ],
      screenshots: [
        screenshot(
          'notifications-settings',
          'Notification template configuration',
        ),
        screenshot('notifications-discord', 'Discord notification'),
        screenshot('notifications-email', 'Email notification'),
      ],
    },
    {
      id: 'home',
      icon: Globe,
      heading: t('homepage.section.home.heading'),
      description: t('homepage.section.home.description'),
      premium: true,
      bullets: visibleBullets(
        t('homepage.section.home.bullets', {
          returnObjects: true,
        }) as RawBullet[],
        gameVersion,
      ),
      integrations: [],
      screenshots: [
        screenshot('custom-domain-1', 'Craft a site that looks like this...'),
        screenshot('custom-domain-2', 'Or this!'),
      ],
    },
  ]

  return (
    <Main fullWidth>
      <HeroSection />

      <div className='root-width flex flex-col gap-20 pt-16'>
        <section className='flex flex-col gap-8'>
          <Reveal className='flex flex-col items-center gap-2 text-center'>
            <h2 className='text-3xl font-medium mb-0'>
              {t('homepage.features-heading')}
            </h2>
          </Reveal>
          <FeatureGrid
            features={features.filter(
              (f) => !f.liveOnly || gameVersion === 'live',
            )}
          />
        </section>

        <GameVersions />
      </div>

      <div className='flex flex-col'>
        {sections
          .filter((s) => !s.liveOnly || gameVersion === 'live')
          .map((section, index) => (
            <FeatureDetailSection
              key={section.heading}
              section={section}
              reversed={index % 2 === 1}
              tinted={index % 2 === 1}
            />
          ))}
      </div>

      <div className='root-width flex flex-col gap-20 py-16'>
        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
          <DiscordCallout />
          <DesktopCallout />
          <ApiCallout />
        </div>

        <CtaSection />
      </div>
    </Main>
  )
}

export default Home
